import { Button, Checkbox, Input, message, notification } from "antd";
import React, { useState, useEffect } from "react";

import pen from "../../../assets/icons/edit-2.svg";
import { controller } from "../../../controller";

export default function BreakdownFinanceLabel({
  status,
  breakdownID,
  basicMajorOrthoData,
  setBasicMajorOrthoData,
  setHasBasciMajorOrthoUpdated,
  patientID,
  sharesMaxWith,
}) {
  const [editBreakdownItem, setEditBreakdownItem] = useState(false);
  const [isEditingBreakdownFinanceItem, setIsEditingBreakdownFinanceItem] = useState(false);
  const [isUnlimited, setIsUnlimited] = useState(
    basicMajorOrthoData[`${status.toLowerCase()}`].overall_max === "9999999.99" ||
    basicMajorOrthoData[`${status.toLowerCase()}`].overall_max === 9999999.99
  );
  const [editedData, setEditedData] = useState({
    overall_max: basicMajorOrthoData[`${status.toLowerCase()}`].overall_max,
    coverage_percentage: basicMajorOrthoData[`${status.toLowerCase()}`].coverage,
    benefits_remaining: basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining,
  });

  // Sync editedData with basicMajorOrthoData and handle special cases
  useEffect(() => {
    setEditedData({
      overall_max: isUnlimited ? "9999999.99" : basicMajorOrthoData[`${status.toLowerCase()}`].overall_max,
      coverage_percentage: basicMajorOrthoData[`${status.toLowerCase()}`].coverage,
      benefits_remaining:
        basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining === "9999999.99" ||
          basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining === 9999999.99
          ? ""
          : basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining,
    });
    setIsUnlimited(
      basicMajorOrthoData[`${status.toLowerCase()}`].overall_max === "9999999.99" ||
      basicMajorOrthoData[`${status.toLowerCase()}`].overall_max === 9999999.99
    );
  }, [basicMajorOrthoData, status]);

  // Handle Unlimited checkbox change
  const handleUnlimitedChange = (checked) => {
    setIsUnlimited(checked);
    if (!checked) {
      setEditedData((prevData) => ({ ...prevData, overall_max: "" }));
    } else {
      setEditedData((prevData) => ({ ...prevData, overall_max: "9999999.99" }));
    }
  };

  const breakdownsItems = [
    { key: "overall_max", label: "Overall Max", value: basicMajorOrthoData[`${status.toLowerCase()}`].overall_max },
    { key: "coverage_percentage", label: "Coverage Percentage", value: basicMajorOrthoData[`${status.toLowerCase()}`].coverage },
    { key: "benefits_remaining", label: "Remaining Benefits", value: basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining },
  ];

  async function handleSubmitEditBreakdownFinanceItem() {
    if (!breakdownID) {
      notification.error({
        message: `Error Editing Breakdown`,
        description: `Please Upload a File or Add a Policy Item to Create a Breakdown first`,
        placement: "top",
        duration: 5,
      });
      return;
    }

    // Return an error if decimal number is more than 2 digits
    if (editedData.coverage_percentage && editedData.coverage_percentage.includes(".") && editedData.coverage_percentage.split(".")[1].length > 2) {
      message.error("Coverage value must be a number with no more than two decimal places");
      return;
    }

    // Return an error if coverage is not between 0 and 100
    if (editedData.coverage_percentage && (Number(editedData.coverage_percentage) < 0 || Number(editedData.coverage_percentage) > 100)) {
      message.error("Coverage must be between 0 and 100");
      return;
    }

    // Validate overall_max if not unlimited
    if (!isUnlimited) {
      if (editedData.overall_max && isNaN(editedData.overall_max)) {
        notification.error({
          message: "Invalid Overall Max",
          description: "Please enter a valid number",
          placement: "top",
          duration: 5,
        });
        return;
      }
      if (editedData.overall_max && editedData.overall_max.includes(".") && editedData.overall_max.split(".")[1].length > 2) {
        message.error("Overall Max must be a number with no more than two decimal places");
        return;
      }
      if (editedData.overall_max && Number(editedData.overall_max) < 0) {
        message.error("Overall Max must be non-negative");
        return;
      }
    }

    // Validate benefits_remaining
    if (editedData.benefits_remaining && isNaN(editedData.benefits_remaining)) {
      notification.error({
        message: "Invalid Remaining Benefits",
        description: "Please enter a valid number",
        placement: "top",
        duration: 5,
      });
      return;
    }
    if (editedData.benefits_remaining && editedData.benefits_remaining.includes(".") && editedData.benefits_remaining.split(".")[1].length > 2) {
      message.error("Remaining Benefits must be a number with no more than two decimal places");
      return;
    }
    if (editedData.benefits_remaining && Number(editedData.benefits_remaining) < 0) {
      message.error("Remaining Benefits must be non-negative");
      return;
    }

    try {
      setIsEditingBreakdownFinanceItem(true);

      const dataToSend = {
        [`${status.toLowerCase()}_overall_max`]: isUnlimited ? 9999999.99 : editedData.overall_max,
        [`${status.toLowerCase()}_percentage`]: editedData.coverage_percentage,
        [`${status.toLowerCase()}_benefits_remaining`]: editedData.benefits_remaining,
      };

      const policyID = "";

      // const response = await controller.updateBreakdownPatientCoverage(patientID, dataToSend);
      const response = await controller.updateBreakdownPatientCoverage(policyID, dataToSend);

      if (response.status < 250) {
        const updatedBreakdownItemValues = {
          [status.toLowerCase()]: {
            overall_max: response.json[`${status.toLowerCase()}_overall_max`],
            coverage: response.json[`${status.toLowerCase()}_percentage`],
            benefits_remaining: response.json[`${status.toLowerCase()}_benefits_remaining`],
          },
        };

        setBasicMajorOrthoData((prevData) => ({ ...prevData, ...updatedBreakdownItemValues }));
        setHasBasciMajorOrthoUpdated(true);
        setEditBreakdownItem(false);
      }
    } catch (err) {
      message.error("Something went wrong while editing breakdown");
      console.error(err);
    } finally {
      setIsEditingBreakdownFinanceItem(false);
    }
  }

  function handleCancelEdit() {
    setEditBreakdownItem(false);
    setIsUnlimited(
      basicMajorOrthoData[`${status.toLowerCase()}`].overall_max === "9999999.99" ||
      basicMajorOrthoData[`${status.toLowerCase()}`].overall_max === 9999999.99
    );
    setEditedData({
      overall_max: isUnlimited ? "9999999.99" : basicMajorOrthoData[`${status.toLowerCase()}`].overall_max,
      coverage_percentage: basicMajorOrthoData[`${status.toLowerCase()}`].coverage,
      benefits_remaining:
        basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining === "9999999.99" ||
          basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining === 9999999.99
          ? ""
          : basicMajorOrthoData[`${status.toLowerCase()}`].benefits_remaining,
    });
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        background: "rgba(223, 218, 255, 0.7)",
        borderRadius: 8,
        paddingInline: 14,
        paddingTop: 12,
        paddingBottom: editBreakdownItem ? 10 : 12,
      }}
      className="breakdown-label"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          fontSize: 17,
          fontWeight: 400,
          color: "#4D3280",
          marginBottom: editBreakdownItem ? 6 : 8,
        }}
      >
        {status}

        {!editBreakdownItem ? (
          <Button onClick={() => setEditBreakdownItem(true)} style={{ width: 28, height: 28, backgroundColor: "transparent", border: "none" }}>
            <img src={pen} alt="Pen" />
          </Button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <Checkbox checked={isUnlimited} onChange={(e) => handleUnlimitedChange(e.target.checked)} />
              Unlimited
            </label>
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: editBreakdownItem ? 6 : 7, width: "100%" }}>
        {breakdownsItems.map((item) =>
          !editBreakdownItem ? (
            <BreakdownItemLabel key={item.label} label={item.label} field={item.value} isUnlimited={isUnlimited} />
          ) : (
            <BreakdownItemLabelEdit
              key={item.key}
              disabled={isEditingBreakdownFinanceItem || (item.key === "overall_max" && isUnlimited)}
              label={item.label}
              itemKey={item.key}
              initialValue={item.value}
              setEditedData={setEditedData}
              editedData={editedData}
              isUnlimited={isUnlimited} // Pass isUnlimited to BreakdownItemLabelEdit
            />
          )
        )}
        {editBreakdownItem && (
          <div style={{ display: "flex", alignItems: "center", marginLeft: "auto", gap: 10, marginTop: 1 }}>
            <Button onClick={handleCancelEdit} disabled={isEditingBreakdownFinanceItem} style={{ backgroundColor: "transparent", border: "none" }}>
              Cancel
            </Button>

            <Button type="primary" onClick={handleSubmitEditBreakdownFinanceItem} disabled={isEditingBreakdownFinanceItem}>
              Save
            </Button>
          </div>
        )}
      </div>

      {Array.isArray(sharesMaxWith) && sharesMaxWith.length > 0 && (
        <div style={{ fontSize: 12, color: "#6b5fa8", marginTop: 10 }}>
          Shares annual max with: {sharesMaxWith.join(", ")}
        </div>
      )}
    </div>
  );
}

function BreakdownItemLabel({ label, field, isUnlimited }) {
  const formattedField = field
    ? label === "Remaining Benefits" && isUnlimited
      ? "-"
      : field === "9999999.99" || field >= 9000000
        ? label === "Overall Max"
          ? "Unlimited"
          : label === "Remaining Benefits"
            ? "-"
            : field
        : label === "Coverage Percentage"
          ? `${field}%`
          : `$${field}`
    : "-";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 5,
        fontSize: 13,
        padding: "6px 12px",
        backgroundColor: "#cfc2f3",
        color: "#403280",
        fontWeight: 600,
        textTransform: "capitalize",
        width: "100%",
      }}
    >
      {label} <span>{formattedField}</span>
    </div>
  );
}

function BreakdownItemLabelEdit({ label, setEditedData, editedData, initialValue, itemKey, disabled, isUnlimited }) {
  const displayValue =
    itemKey === "overall_max" && isUnlimited
      ? "Unlimited"
      : editedData[itemKey] !== undefined && editedData[itemKey] !== null
        ? editedData[itemKey]
        : itemKey === "benefits_remaining" && (initialValue === "9999999.99" || initialValue === 9999999.99)
          ? ""
          : initialValue !== undefined && initialValue !== null
            ? initialValue.toString()
            : "";

  return (
    <div className="breakdown-item-edit">
      <span>{label}</span>
      <Input
        value={displayValue}
        style={{ height: 34 }}
        disabled={disabled}
        onChange={(e) => setEditedData((prevData) => ({ ...prevData, [itemKey]: e.target.value }))}
      />
    </div>
  );
}
